import type { Recipe } from "@/features/Recipes-renamed/types";
import type { Database } from "./database.types";
import type { Direction, DirectionGroup, IngredientGroup, Ingredient } from "@/lib/types/recipe";
import { mapCategoryRowToCategory, type RecipeCategoryRow } from "@/hooks/useGetRecipeCategories";
import type { RecipeTypeRow } from "@/hooks/useGetRecipeTypes";

type RecipesRow = Database["public"]["Tables"]["recipes"]["Row"];
type IngredientsRow = Database["public"]["Tables"]["ingredients"]["Row"];
type IngredientGroupsRow = Database["public"]["Tables"]["ingredient_groups"]["Row"] & {
    ingredients: IngredientsRow[];
};
export type RecipesRowWithRelations = RecipesRow & {
    ingredient_groups: IngredientGroupsRow[];
    recipe_categories: RecipeCategoryRow[];
    recipe_types: RecipeTypeRow;
};

// Directions are stored as a JSON column. The current shape is DirectionGroup[],
// but older rows (pre-grouping) were a flat Direction[]. Wrap any legacy flat
// array in a single untitled group so the display path always sees groups.
function normalizeDirections(raw: unknown): DirectionGroup[] {
    if (!Array.isArray(raw)) return [];
    const isFlat = raw.length > 0 && raw.every((item) => item && typeof item === "object" && "step" in item);
    if (isFlat) return [{ steps: raw as Direction[] }];
    return raw as DirectionGroup[];
}

export function mapDatabaseRecipeToRecipe(recipe: RecipesRowWithRelations): Recipe {
    return {
        id: recipe.id,
        name: recipe.name,
        description: recipe.description ?? "",
        image: recipe.image_url ?? "",
        hasMadeIt: recipe.has_made_it,
        favorite: recipe.favorite,
        isYield: recipe.is_yield,
        prepTime: recipe.prep_time,
        cookTime: recipe.cook_time,
        totalTime: recipe.total_time,
        servings: recipe.servings,
        temperature: recipe.temperature,
        recipeTypeId: recipe.recipe_type_id,
        recipeType: recipe.recipe_types,
        categories: recipe.recipe_categories.map(mapCategoryRowToCategory),
        notes: recipe.notes ?? "",
        storage: recipe.storage ?? "",
        sourceUrl: recipe.source_url ?? "",
        sourceDomainName: recipe.source_domain_name ?? "",
        createdAt: recipe.created_at,
        directions: normalizeDirections(recipe.directions),
        ingredientGroups: recipe.ingredient_groups.map<IngredientGroup>((group) => ({
            id: group.id,
            title: group.title ?? "",
            ingredients: group.ingredients.map<Ingredient>((ingredient) => ({
                id: ingredient.id,
                name: ingredient.name,
                quantity: ingredient.quantity ?? "",
                unit: ingredient.unit ?? "",
                notes: ingredient.notes ?? "",
                optional: ingredient.optional
            }))
        }))
    };
}
